import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuthStore } from '@/store/authStore';

// Keyboard Shortcut System
import { 
  ShortcutProvider, 
  useShortcuts, 
  useRegisterShortcuts, 
  KeyboardShortcutsHelp, 
  allShortcuts,
  setNavigateFunction 
} from '@/components/keyboard';
// Pages
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';

import { TasksPage } from '@/components/features/tasks/TasksPage';
import { InvoicingPage } from '@/components/features/invoicing/InvoicingPage';
import { ReportsPage } from '@/components/features/reports/ReportsPage';
import { ConfigPage } from '@/components/features/configurations/ConfigPage';
import { ProfilePage } from '@/components/features/profile/ProfilePage';

// App Content Component with Router Integration
const AppContent: React.FC = () => {
  const { isHelpOpen, hideHelp } = useShortcuts();
  const navigate = useNavigate();
  
  // Set the navigate function for shortcuts
  useEffect(() => {
    console.log('🔗 Registering navigate function for shortcuts');
    setNavigateFunction(navigate);
  }, [navigate]);
  
  // Register all keyboard shortcuts
  useRegisterShortcuts(allShortcuts);

  return (
    <>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
            },
          },
        }}
      />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Protected Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/tasks" replace />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="team-tasks" element={<TasksPage />} />
          <Route path="all-tasks" element={<TasksPage />} />
          <Route path="invoicing" element={<InvoicingPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="configurations/*" element={<ConfigPage />} />
          <Route path="profile/*" element={<ProfilePage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/tasks" replace />} />
      </Routes>

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp isOpen={isHelpOpen} onClose={hideHelp} />
    </>
  );
};

// Main App Component
function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      checkAuth().finally(() => {
        setIsInitialized(true);
      });
    }
  }, [isInitialized, checkAuth]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ShortcutProvider>
        <AppContent />
      </ShortcutProvider>
    </Router>
  );
}

export default App;